import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  Truck,
  Users,
  Briefcase,
  Wrench,
  Fuel,
  CalendarCheck,
  Wallet,
  HandCoins,
  FileBarChart2,
  MessageSquare,
  Settings,
  LogOut,
  ChevronDown,
  MoreHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth-store';
import { authApi } from '@/features/auth/api';

type NavItem = { to: string; label: string; icon: LucideIcon };

const navItems: readonly NavItem[] = [
  { to: '/command-center', label: 'Command Center', icon: Activity },
  { to: '/assets', label: 'Assets', icon: Truck },
  { to: '/field-users', label: 'Field Users', icon: Users },
  { to: '/engagements', label: 'Engagements', icon: Briefcase },
  { to: '/maintenance', label: 'Maintenance', icon: Wrench },
  { to: '/fuel', label: 'Fuel', icon: Fuel },
  { to: '/attendance', label: 'Attendance', icon: CalendarCheck },
  { to: '/payroll', label: 'Payroll', icon: Wallet },
  { to: '/advances', label: 'Advances', icon: HandCoins },
  { to: '/reports', label: 'Reports', icon: FileBarChart2 },
  { to: '/comms', label: 'Comms', icon: MessageSquare },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

// The first six items render inline; the rest collapse into a "More ▾"
// dropdown so the bar never needs to scroll horizontally.
const PRIMARY_COUNT = 6;
const primaryItems = navItems.slice(0, PRIMARY_COUNT);
const overflowItems = navItems.slice(PRIMARY_COUNT);

function initialsOf(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

export function AppLayout() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clearSession = useAuthStore((s) => s.clearSession);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const userRecord = (user ?? {}) as Record<string, unknown>;
  const tenantRecord = (tenant ?? {}) as Record<string, unknown>;
  const displayName =
    (typeof userRecord.fullName === 'string' && userRecord.fullName) ||
    (typeof userRecord.display_name === 'string' && userRecord.display_name) ||
    (typeof userRecord.full_name === 'string' && userRecord.full_name) ||
    (typeof userRecord.email === 'string' && userRecord.email) ||
    'Signed in';
  const displayTenant =
    (typeof tenantRecord.tenantName === 'string' && tenantRecord.tenantName) ||
    (typeof tenantRecord.name === 'string' && tenantRecord.name) ||
    '';

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      if (refreshToken) {
        await authApi.logout(refreshToken).catch(() => {
          /* fall through to local clear */
        });
      }
    } finally {
      // Forcefully clear auth state regardless of the API outcome. The
      // persisted store writes to localStorage synchronously here, which also
      // fires a `storage` event that signs every other open tab out (see the
      // cross-tab listener in RootLayout).
      clearSession();
      // Redirect with a HARD document navigation rather than a client-side
      // route change. From the Command Center, a soft SPA transition unmounts
      // the live Leaflet map mid-commit; if the map is animating or has an open
      // popup, its teardown throws inside React's commit phase, which leaves
      // the route half-changed — the modal overlay and map stay on screen, the
      // UI freezes, and /login never renders. A full reload hands teardown to
      // the browser instead of React's reconciler, so logout can't get stuck.
      window.location.replace('/login');
    }
  };

  // Command Center is a full-bleed surface; everything else gets the
  // page container so content has consistent padding and a sane max-width.
  const isFullBleed = location.pathname.startsWith('/command-center');

  // Highlight the "More" button when one of its hidden routes is active.
  const overflowActive = overflowItems.some((item) => location.pathname.startsWith(item.to));

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
        <div className="mx-auto flex h-14 w-full max-w-[1600px] items-center gap-1.5 px-3 xl:gap-2 xl:px-5">
          {/* Brand zone */}
          <div className="flex shrink-0 items-center gap-2">
            <img
              src="/logo.png"
              alt="Fleet Iris"
              className="h-7 w-auto select-none"
              draggable={false}
            />
            <span className="live-pill hidden xl:inline-flex" title="Real-time telemetry active">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
              </span>
              Live
            </span>
          </div>

          <span className="nav-divider" aria-hidden="true" />

          {/* Nav zone */}
          <nav className="flex items-center gap-0.5 overflow-visible" aria-label="Primary">
            {primaryItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => cn('nav-item', isActive && 'active')}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}

            {overflowItems.length > 0 && (
              <div className="group relative">
                <button
                  type="button"
                  className={cn('nav-item', overflowActive && 'active')}
                  aria-haspopup="menu"
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  <span>More</span>
                  <ChevronDown
                    className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180"
                    aria-hidden="true"
                  />
                </button>
                {/* The pt-2 wrapper is an invisible hover-bridge: it keeps the
                    menu inside the hovered group so the gap below the button
                    doesn't flicker the dropdown closed. */}
                <div className="invisible absolute right-0 top-full z-40 pt-2 opacity-0 transition-opacity duration-150 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
                  <div className="nav-dropdown" role="menu">
                    {overflowItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          role="menuitem"
                          className={({ isActive }) =>
                            cn('nav-dropdown-item', isActive && 'active')
                          }
                        >
                          <Icon className="h-4 w-4" aria-hidden="true" />
                          <span>{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </nav>

          {/* User-profile zone */}
          <div className="ml-auto flex shrink-0 items-center gap-1.5 xl:gap-2">
            <span className="nav-divider" aria-hidden="true" />
            {tenant && (
              <div
                className="flex items-center gap-2 rounded-full border border-border bg-background/60 py-1 pl-1 pr-2.5"
                title={`Tenant: ${displayTenant || '—'} · Role: ${tenant.role}`}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary ring-1 ring-primary/25">
                  {initialsOf(displayName !== 'Signed in' ? displayName : displayTenant)}
                </span>
                <span className="hidden flex-col leading-tight xl:flex">
                  <span className="text-xs font-semibold text-foreground">{displayName}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {displayTenant ? `${displayTenant} · ${tenant.role}` : tenant.role}
                  </span>
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="btn-ghost btn-sm"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden xl:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        {isFullBleed ? (
          <Outlet />
        ) : (
          <div className="mx-auto w-full max-w-[1600px]">
            <Outlet />
          </div>
        )}
      </main>

      {confirmOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-confirm-title"
        >
          <div className="modal space-y-4">
            <div>
              <h2 id="logout-confirm-title" className="text-lg font-semibold">
                Log out?
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Are you sure you want to log out
                {displayName !== 'Signed in' ? ` as ${displayName}` : ''}
                {displayTenant ? ` from ${displayTenant}` : ''}? You'll need to sign in again to
                continue.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={loggingOut}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                className="btn-danger"
              >
                {loggingOut ? 'Logging out…' : 'Yes, log out'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
